/* eslint-disable @typescript-eslint/no-unused-vars */
import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  input,
  WritableSignal,
  model,
  signal,
  effect,
} from '@angular/core';
import { Icon } from '../icon/icon.component';
import { ToastService } from '../../services/toast.service';

/**
 * @name FileDownloadComponent
 * @description 브라우저에서 파일 다운로드를 지원하는 컴포넌트
 */
@Component({
  selector: 'lepi-file-download',
  imports: [Icon, CommonModule],
  template: `
    <button (click)="downloadFile($event)" class="w-full">
      @if (variant() === 'product') {
        <!-- Product 스타일 - 투자설명서 다운로드 -->
        <div
          class="cursor-pointer flex items-center justify-between py-3 px-4 bg-white border border-gray-100 rounded-2xl hover:border-primary/20 active:scale-[0.99] transition-all group">
          <div class="flex items-center gap-4 flex-1 min-w-0">
            <div
              class="size-11 bg-primary/5 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-primary/10 transition-colors">
              <lepi-icon
                [name]="
                  isLoading()
                    ? 'line-md:downloading-loop'
                    : 'material-symbols:description'
                "
                class="size-5! text-primary" />
            </div>
            <div class="flex flex-col min-w-0 text-left justify-center">
              <span class="text-sm text-gray-800 font-bold"
                >투자설명서 및 증권신고서</span
              >
            </div>
          </div>
          <div class="flex items-center gap-2 shrink-0 ml-3">
            @if (displaySize() > 0) {
              <span class="text-xs text-gray-400 font-medium">
                {{ fileSizeFormat(displaySize()) }}
              </span>
            }
            <div
              class="size-9 bg-gray-50 rounded-xl flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
              <lepi-icon
                [name]="
                  isLoading()
                    ? 'line-md:downloading-loop'
                    : 'material-symbols:download-rounded'
                "
                class="size-5!" />
            </div>
          </div>
        </div>
      } @else {
        <!-- 기본 스타일 -->
        <div
          class="overflow-hidden justify-between bg-white rounded-md w-full text-xs text-gray-500 cursor-pointer flex gap-3 items-center border border-primary-500 p-3 min-w-50">
          <div class="flex gap-2 items-center">
            <lepi-icon
              [name]="
                isLoading() ? 'line-md:downloading-loop' : 'line-md:downloading'
              " />
            <p class="truncate max-w-60">{{ filename() }}</p>
          </div>
          @if (displaySize() > 0) {
            <p>{{ fileSizeFormat(displaySize()) }}</p>
          }
        </div>
      }
    </button>
  `,
})
export default class FileDownloadComponent {
  readonly toastService = inject(ToastService);

  url = input.required<string>();
  size = input<number>(0);
  filename = input<string>('file');
  variant = input<'default' | 'product'>('default');

  // 파일 크기 (입력값 또는 자동 조회값)
  private fetchedSize = signal<number>(0);
  displaySize = () => this.size() || this.fetchedSize();

  /**
   * @description 파일 다운로드 로딩상태
   */
  isLoading: WritableSignal<boolean> = model<boolean>(false);

  constructor() {
    // URL이 변경되면 파일 크기 조회
    effect(() => {
      const currentUrl = this.url();
      const currentSize = this.size();

      // size가 제공되지 않은 경우에만 자동 조회
      if (currentUrl && !currentSize) {
        this.fetchFileSize(currentUrl);
      }
    });
  }

  /**
   * @name fetchFileSize
   * @description HEAD 요청으로 파일 크기 조회
   */
  private async fetchFileSize(url: string): Promise<void> {
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        mode: 'cors',
      });
      const contentLength = response.headers.get('Content-Length');
      if (contentLength) {
        this.fetchedSize.set(parseInt(contentLength, 10));
      }
    } catch (error) {
      console.warn('Failed to fetch file size:', error);
      this.fetchedSize.set(0);
    }
  }

  /**
   * @name downloadFile
   * @description 브라우저에서 파일 다운로드
   */
  async downloadFile(ev?: Event): Promise<void> {
    this.isLoading.set(true);
    try {
      await this.downloadForWeb();
    } catch (error) {
      console.error('다운로드에 실패했습니다.', error);
      this.toastService.error('다운로드에 실패했습니다.');
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * @name downloadForWeb
   * @description 웹 브라우저에서 파일 다운로드
   */
  private async downloadForWeb(): Promise<void> {
    if (!this.url()) {
      console.error('다운로드할 URL이 없습니다.');
      this.toastService.error('다운로드할 URL이 없습니다.');
      return;
    }

    const url = await this.getDownloadUrl();
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = this.filename();
    document.body.appendChild(a);
    a.click();

    // revokeObjectURL 지연
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      a.remove();
    }, 1000);
  }

  /**
   * @name getDownloadUrl
   * @description 다운로드용 URL 생성
   */
  private async getDownloadUrl(): Promise<string> {
    try {
      const res = await fetch(this.url(), {
        method: 'GET',
        headers: { 'Cache-Control': 'no-cache' },
        mode: 'cors',
        referrerPolicy: 'no-referrer-when-downgrade',
      });
      const blob = await res.blob();
      return window.URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error getting download URL:', error);
      return this.url();
    }
  }

  public fileSizeFormat(size: number): string {
    if (size < 1024) return `${size}B`;
    else if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)}KB`;
    else if (size < 1024 * 1024 * 1024)
      return `${(size / (1024 * 1024)).toFixed(0)}MB`;
    else return `${(size / (1024 * 1024 * 1024)).toFixed(2)}GB`;
  }
}
